import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Flame, MessageSquare, X, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '+91-90000-00000';
const POLL_MS = 25000;
const DISMISS_KEY = 'sanmiggrc-hot-lead-dismissed-ts';

// sessionStorage holds only a non-sensitive UI-state flag (a dismissed-timestamp).
// No PII, no auth tokens, no business data — XSS exposure here is informational only.
const getDismissed = () => {
  try {
    return window.sessionStorage.getItem(DISMISS_KEY);
  } catch (error) {
    console.warn('HotLeadNotifier: sessionStorage read failed', error);
    return null;
  }
};

const setDismissed = (ts) => {
  try {
    window.sessionStorage.setItem(DISMISS_KEY, ts);
  } catch (error) {
    console.warn('HotLeadNotifier: sessionStorage write failed', error);
  }
};

const buildWhatsAppLink = (item) => {
  const phone = WHATSAPP_NUMBER.replace(/[^0-9]/g, '');
  const body = `Hi Sanjeev — incoming via Ask Sanjeev AI:\n\n"${item.question}"`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(body)}`;
};

const HotLeadNotifier = () => {
  const [item, setItem] = useState(null);
  const [hidden, setHidden] = useState(false);

  const fetchHot = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/ask-sanjeev/recent-hot`, { params: { limit: 1 } });
      const next = res.data?.items?.[0] || null;
      if (!next) {
        setItem(null);
        return;
      }
      setHidden(getDismissed() === next.created_at);
      setItem(next);
    } catch {
      setItem(null);
    }
  }, []);

  useEffect(() => {
    fetchHot();
    const t = setInterval(fetchHot, POLL_MS);
    return () => clearInterval(t);
  }, [fetchHot]);

  const handleDismiss = () => {
    if (item?.created_at) setDismissed(item.created_at);
    setHidden(true);
  };

  if (!item || hidden) return null;

  const isHot = item.priority === 'hot';
  const waLink = buildWhatsAppLink(item);

  return (
    <div
      data-testid="hot-lead-notifier"
      className={`relative overflow-hidden rounded-xl border shadow-sm ${
        isHot
          ? 'bg-gradient-to-r from-rose-50 via-pink-50 to-amber-50 border-rose-300'
          : 'bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 border-amber-300'
      }`}
    >
      {isHot && <div className="absolute -left-2 top-0 bottom-0 w-1 bg-rose-500 animate-pulse" />}
      <div className="flex items-start gap-4 p-4">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isHot ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
          }`}
        >
          <Flame className={`w-5 h-5 ${isHot ? 'animate-pulse' : ''}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={isHot ? 'bg-rose-600 text-white' : 'bg-amber-500 text-white'}>
              {isHot ? 'Hot Lead' : 'Warm Lead'}
            </Badge>
            {item.matched?.slice(0, 4).map((k) => (
              <Badge key={k} variant="outline" className="text-[10px] capitalize">
                {k}
              </Badge>
            ))}
            <span className="text-[11px] text-gray-500">
              {new Date(item.created_at).toLocaleTimeString('en-IN', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>
          <p className="text-sm text-gray-900 mt-1.5 line-clamp-2">
            <span className="font-semibold">Inbound from Ask Sanjeev AI:</span> &ldquo;{item.question}&rdquo;
          </p>
          <div className="flex items-center gap-2 mt-3">
            <a
              href={waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hot-lead-whatsapp"
              className="inline-flex items-center px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium"
            >
              <MessageSquare className="w-3.5 h-3.5 mr-1.5" />
              WhatsApp Sanjeev now
            </a>
            <Link
              to="/lead-hub"
              data-testid="hot-lead-open"
              className="inline-flex items-center px-3 py-1.5 rounded-md bg-white border border-gray-200 hover:border-gray-300 text-xs font-medium text-gray-700"
            >
              Open Lead Hub <ArrowRight className="w-3 h-3 ml-1" />
            </Link>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          data-testid="hot-lead-dismiss"
          className="p-1 rounded hover:bg-white/60"
          title="Dismiss"
        >
          <X className="w-4 h-4 text-gray-500" />
        </button>
      </div>
    </div>
  );
};

export default HotLeadNotifier;
